// One caster in the line of a caster select, as a key: its symbol, its name,
// and a circle in its corner that takes a check when the caster is picked.
// Garrett, 2026-10-14: "casters should look like buttons here instead of
// floating panels", and "they should have checkmarks instead of number 1/2
// when selecting, make it a little circle checkmark thing to FEEL like a
// selection interface". With a mouse, the pointer on it tells it in the
// pane and a click picks it. On a touch screen a tap only tells it, and the
// button of the pane picks. The select of a match against the AI and the lobby
// of a room both use it.

import type { Caster } from '../content/casters';
import { el } from '../lib/dom';
import { createCasterPawn } from './CasterMark';
import { createKeyIcon } from './keyIcons';

export type CasterChoice = { caster: Caster; root: HTMLButtonElement; flag: HTMLElement };

export type CasterChoiceActions = {
  /** The pane tells this caster now. */
  tell: (caster: Caster) => void;
  /** A click with a mouse or a key: the caster goes in, or back. */
  toggle: (caster: Caster) => void;
  /** The caster the pane tells now. */
  told: () => Caster | undefined;
};

export const createCasterChoice = (caster: Caster, actions: CasterChoiceActions): CasterChoice => {
  // The last press on the caster was a touch. Then a click on it only fills the pane.
  let byTouch = false;
  const flag = el('span', { className: 'caster-select__flag' }, [createKeyIcon('check')]);
  const root = el(
    'button',
    {
      className: 'caster-select__caster',
      type: 'button',
      ariaPressed: 'false',
      onpointerdown: (event: PointerEvent) => {
        byTouch = event.pointerType === 'touch';
      },
      // A touch has no hover, so the first thing a tap does is tell the caster.
      onpointerenter: (event: PointerEvent) => {
        if (event.pointerType === 'mouse' && actions.told() !== caster) actions.tell(caster);
      },
      onfocus: () => {
        if (actions.told() !== caster) actions.tell(caster);
      },
      onclick: () => {
        if (byTouch) actions.tell(caster);
        else actions.toggle(caster);
      },
    },
    [
      createCasterPawn(caster.id),
      el('span', { className: 'caster-select__tile-name', textContent: caster.name }),
      flag,
    ],
  );
  root.dataset.caster = caster.id;
  return { caster, root, flag };
};
